import { TrialBalanceTemplate } from './templates/financial-statement/tb/tb.template';
import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx-js-style';
import {
  AllCompaniesRoundsByPeriodTemplate,
  SingleCompanyRoundsByPeriodTemplate,
} from './templates/companies-rounds-by-period';
import { ExcelData, ExcelOptions, ExcelTemplate, ExcelTemplateName } from './interfaces';
import { CashOnCashTemplate } from './templates/cash-on-cash/cash-on-cash.template';
import { OverviewTemplate } from './templates/portfolio-overview/portfolio-overview.template';
import { CompaniesTemplate } from './templates/companies/companies.template';
import { AssemblyTemplate } from './templates/financial-statement/assembly/assembly.template';
import { FinancialStatementTemplate } from './templates/financial-statement/financialStatement.template';
import {
  FundPerformanceHighLevelTableTemplate,
} from './templates/fund-performnace-hight-level/fund-performances-high-level.template';
import { CommitmentAllocationTemplate } from './templates/commitment-allocation/commitment-allocation.template';
import { CashFlowTemplate } from './templates/cashflow/casfFlow.template';
import { PeriodComparisonTemplate } from './templates/comparison/period-comparison';
import { CapitalAccountTemplate } from './templates/financial-statement/capital-account/capital-account.template';
import { InvestorContributionTemplate } from './templates/investor-contribution/investorContributionTemplate';
import {
  InitialFollowOnInvestmentTemplate,
} from './templates/initial-follow-on-investment-template/initialFollowOnInvestmentTemplate';
import { CompanyMembersTemplate } from './templates/company-members/company-members-template';
import { PpByFundTemplate } from 'src/app/services/excel/templates/pp-by-fund/ppByFundTemplate';
import { GeneralLedgerTemplate } from './templates/financial-statement/gl/gl.template';
import { BalanceSheetTemplate } from 'src/app/services/balance-sheet/bsTemplate';
import { ProfitAndLossTemplate } from './templates/financial-statement/profit-and-loss/profitAndLossTemplate';
import { SharedDetailsTemplate } from './templates/shared-details/SharedDetailsTemplate';
import { ValuationBasisTemplate } from './templates/valuation-basis/ValuationBasisTemplate';
import { ComparisonByValueTemplate } from './templates/comparison-by/comparison-by-values';
import { RoundComparisonTemplate } from './templates/round-comparison/round-comparison-template';
import { FollowOnModelTableTemplateStepThree } from './templates/follow-on-table/FollowOnModelTabletemplate';
import { FollowModelExcelExportStepTwo } from './templates/follow-on-table/step-two/FollowOnModelTemplateStepTwo';
import { FollowOnTableAllStepsTemplate } from './templates/follow-on-table/FollowOnTableAllStepsTemplate';
import { PpAnalysisAtCostTemplate } from './templates/pp-analysis-at-cost/PpAnalysisAtCostTemplate';
import { FundsAndPerformanceTemplate } from './templates/funds-and-performance/fundsAndPerfromanceTemplate';
import { DataBehindFvAndOpCharts } from './templates/data-behind-fv-and-pp-charts/dataBehindFvAndOpCharts';
import { CashFlowDealFlowTemplate } from './templates/cash-on-cash/cash-flow-matrix-template';
import { MaVintageTableTemplate } from './templates/market-analisys/MaVintageTableTemplate';
import { MaFundsSettingsTemplate } from './templates/market-analisys/MaFundsSettings/maFundsSettingsTemplate';
import { MaAllRoundsTemplate } from './templates/market-analisys/ma-all-rounds/MaAllRoundsTemplate';
import { MaByIndustryTemplate } from './templates/market-analisys/ma-by-industry/MaByIndustryTemplate';
import { MaAllByCompanyTemplate } from './templates/market-analisys/ma-by-company/maByCompanyTemplate';
import { FpActiveTableTemplate } from './templates/fund-performances/fp-active-table/fpActiveTableTemplate';
import { KpiPortfolioAnalysisTemplate } from './templates/metric/kpi-portfolio-analysis/KpiPortfolioAnalysisTemplate';
import { KpiCrossCompanyTemplate } from './templates/metric/kpi-cross-company/KpiCrossCompanyTemplate';
import { FundStrategyInternalTemplate } from './templates/fund-strategy/internal/fundStrategyInternalTemplate';
import { FundStrategyExternalTemplate } from './templates/fund-strategy/external/fundStrategyExternalTemplate';
import { ComparableCompanyValuation } from './templates/comparable-company-value/comparableCompanyValuation';
import { BusinessEvaluationTemplate } from './templates/business-evaluation/business-evaluation.template';
import { UserRecoveryTemplate } from './templates/users-recovery/userRecoveryTemplate';
import { FundTemplate } from './templates/funds/fundTemplate';
import { SoiByOrgTemplate } from './templates/soi/soi-by-org/SoiByOrgTemplate';
import { SoiTemplateByFund } from './templates/soi/soi-by-fund/soi.template';
import { SoiALlPeriodsTemplate } from './templates/soi/soi-all-periods/soiAllPeriodsTemplate';

/**
 * Service for work with Excel sheets.
 * If you have any questions ask Yan plz.
 * This is really tough shit.
 *
 * If you want to use this service with your own template please
 * check the template in the 'templates' folder.
 */
@Injectable({
  providedIn: 'root',
})
export class ExcelService {
  private templates: { [k in ExcelTemplateName]: ExcelTemplate } = {
    [ExcelTemplateName.allCompaniesRoundsByPeriod]: new AllCompaniesRoundsByPeriodTemplate(),
    [ExcelTemplateName.singleCompanyRoundsByPeriod]: new SingleCompanyRoundsByPeriodTemplate(),
    [ExcelTemplateName.cashOnCash]: new CashOnCashTemplate(),
    [ExcelTemplateName.overview]: new OverviewTemplate(),
    [ExcelTemplateName.companies]: new CompaniesTemplate(),
    [ExcelTemplateName.assembly]: new AssemblyTemplate(),
    [ExcelTemplateName.gl]: new GeneralLedgerTemplate(),
    [ExcelTemplateName.tb]: new TrialBalanceTemplate(),
    [ExcelTemplateName.financialStatement]: new FinancialStatementTemplate(),
    [ExcelTemplateName.capitalAccount]: new CapitalAccountTemplate(),
    [ExcelTemplateName.fundPerformance]: new FundPerformanceHighLevelTableTemplate(),
    [ExcelTemplateName.commitmentAllocation]: new CommitmentAllocationTemplate(),
    [ExcelTemplateName.cashFlowTemplate]: new CashFlowTemplate(),
    [ExcelTemplateName.investorContributionTemplate]: new InvestorContributionTemplate(),
    [ExcelTemplateName.periodComparisonTemplate]: new PeriodComparisonTemplate(),
    [ExcelTemplateName.initialFollowOnInvestmentTemplate]: new InitialFollowOnInvestmentTemplate(),
    [ExcelTemplateName.companyMembersTemplate]: new CompanyMembersTemplate(),
    [ExcelTemplateName.ppByFundTemplate]: new PpByFundTemplate(),
    [ExcelTemplateName.balanceSheetTemplate]: new BalanceSheetTemplate(),
    [ExcelTemplateName.profitAndLossTemplate]: new ProfitAndLossTemplate(),
    [ExcelTemplateName.sharedDetails]: new SharedDetailsTemplate(),
    [ExcelTemplateName.valuationBasis]: new ValuationBasisTemplate(),
    [ExcelTemplateName.comparisonByValue]: new ComparisonByValueTemplate(),
    [ExcelTemplateName.roundComparison]: new RoundComparisonTemplate(),
    [ExcelTemplateName.followOnModelTableTemplateStepThree]: new FollowOnModelTableTemplateStepThree(),
    [ExcelTemplateName.followOnModelTableStepTwo]: new FollowModelExcelExportStepTwo(),
    [ExcelTemplateName.followOnModelAllSteps]: new FollowOnTableAllStepsTemplate(),
    [ExcelTemplateName.ppAnalysisAtCost]: new PpAnalysisAtCostTemplate(),
    [ExcelTemplateName.fundAndPerformance]: new FundsAndPerformanceTemplate(),
    [ExcelTemplateName.dataBehindFvOpChart]: new DataBehindFvAndOpCharts(),
    [ExcelTemplateName.cashFlowDealFLow]: new CashFlowDealFlowTemplate(),
    [ExcelTemplateName.maVintageTable]: new MaVintageTableTemplate(),
    [ExcelTemplateName.maFundsSettings]: new MaFundsSettingsTemplate(),
    [ExcelTemplateName.maAllRounds]: new MaAllRoundsTemplate(),
    [ExcelTemplateName.maByIndustry]: new MaByIndustryTemplate(),
    [ExcelTemplateName.maByCompany]: new MaAllByCompanyTemplate(),
    [ExcelTemplateName.fpActiveTable]: new FpActiveTableTemplate(),
    [ExcelTemplateName.KpiPortfolioAnalysis]: new KpiPortfolioAnalysisTemplate(),
    [ExcelTemplateName.KpiCrossCompany]: new KpiCrossCompanyTemplate(),
    [ExcelTemplateName.fundStrategyInternal]: new FundStrategyInternalTemplate(),
    [ExcelTemplateName.fundStrategyExternal]: new FundStrategyExternalTemplate(),
    [ExcelTemplateName.companyComparableValuation]: new ComparableCompanyValuation(),
    [ExcelTemplateName.businessEvaluation]: new BusinessEvaluationTemplate(),
    [ExcelTemplateName.usersRecovery]: new UserRecoveryTemplate(),
    [ExcelTemplateName.funds]: new FundTemplate(),
    [ExcelTemplateName.soiByOrg]: new SoiByOrgTemplate(),
    [ExcelTemplateName.soiByFund]: new SoiTemplateByFund(),
    [ExcelTemplateName.soiAllPeriods]: new SoiALlPeriodsTemplate(),
  };

  /**
   * Export data to excel file by template.
   *
   * @param data Data to export.
   * @param fileName File name to export.
   * @param options Options for template.
   * @param templateName Template name.
   */
  exportByTemplate(
    data: ExcelData,
    fileName: string,
    templateName: ExcelTemplateName,
    options: ExcelOptions = {},
  ): void {
    const template = this.templates[templateName];
    const book = template.export(data, options);
    XLSX.writeFile(book, fileName);
  }
}
